<template>
  <section class="conversation-page bg-white dark:bg-slate-900">
    <chat-list
      :show-conversation-list="showConversationList"
      :conversation-inbox="inboxId"
      :label="label"
      :team-id="teamId"
      :conversation-type="conversationType"
      :folders-id="foldersId"
      :is-on-expanded-layout="isOnExpandedLayout"
      @conversation-load="onConversationLoad"
    >
      <pop-over-search
        :is-on-expanded-layout="isOnExpandedLayout"
        @toggle-conversation-layout="toggleConversationLayout"
      />
    </chat-list> 
    <conversation-box
      v-if="showMessageView"
      :inbox-id="inboxId"
      :is-contact-panel-open="isContactPanelOpen"
      :is-on-expanded-layout="isOnExpandedLayout"
      @contact-panel-toggle="onToggleContactPanel"
    />
  </section>
</template>

<script>
import { mapGetters } from 'vuex';
import ChatList from '../../../components/ChatList';
import ConversationBox from '../../../components/widgets/conversation/ConversationBox';
import PopOverSearch from './search/PopOverSearch';
import uiSettingsMixin from 'dashboard/mixins/uiSettings';
import { debounce } from '@chatwoot/utils';
import {
  buildUpdatedAccountPreferences,
  conversationFolderExists,
  getConversationViewDataFromRoute,
  getRouteToSaveLastConversationView,
  inboxExists,
  isSameAccountPreference,
  isSameConversationViewNavigation,
  labelExists,
  teamExists,
} from 'dashboard/helper/userPreferencesHelper';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import wootConstants from 'dashboard/constants/globals';
import { conversationListPageURL } from '../../../helper/URLHelper';

export default {
  components: {
    ChatList,
    ConversationBox,
    PopOverSearch,
  },
  mixins: [uiSettingsMixin],
  props: {
    inboxId: {
      type: [String, Number],
      default: 0,
    },
    conversationId: {
      type: [String, Number],
      default: 0,
    },
    label: {
      type: String,
      default: '',
    },
    teamId: {
      type: String,
      default: '',
    },
    conversationType: {
      type: String,
      default: '',
    },
    foldersId: {
      type: [String, Number],
      default: 0,
    },
  },
  data() {
    return {
      showSearchModal: false,
    };
  },
  computed: {
    ...mapGetters({
      chatList: 'getAllConversations',
      currentChat: 'getSelectedChat',
      appliedConversationFilters: 'getAppliedConversationFilters',
      folders: 'customViews/getCustomViews',
      labels: 'labels/getLabels',
      inboxes: 'inboxes/getInboxes',
      teams: 'teams/getTeams',
    }),
    hasFilterOrFolderView() {
      const hasAppliedFilters = (this.appliedConversationFilters || []).length > 0;
      const hasActiveFolder = this.foldersId && Number(this.foldersId) !== 0;
      return hasAppliedFilters || hasActiveFolder;
    },
    showConversationList() {
      return this.isOnExpandedLayout ? !this.conversationId : true;
    },
    showMessageView() {
      return this.conversationId ? true : !this.isOnExpandedLayout;
    },
    isOnExpandedLayout() {
      const {
        LAYOUT_TYPES: { CONDENSED },
      } = wootConstants;
      const { conversation_display_type: conversationDisplayType = CONDENSED } =
        this.uiSettings;
      return conversationDisplayType !== CONDENSED;
    },
    isContactPanelOpen() {
      if (this.currentChat.id) {
        const { is_contact_sidebar_open: isContactSidebarOpen } =
          this.uiSettings;
        return isContactSidebarOpen;
      }
      return false;
    },
  },
  watch: {
    conversationId(newId, oldId) {
      if (!newId && oldId) {
        this.$store.dispatch('clearSelectedState');
      }
      this.fetchConversationIfUnavailable();
    },
    $route: {
      async handler(to, from) {
        if (!isSameConversationViewNavigation(to, from)) {
          const isValid = await this.ensureValidConversationViewRoute(to);
          if (!isValid) return;
        }

        const routeToSave = getRouteToSaveLastConversationView(to, from);
        if (routeToSave) {
          this.debouncedSaveLastConversationView(routeToSave);
        }
      },
      immediate: false,
    },
  },
  created() {
    if (!this.conversationId) {
      this.$store.dispatch('clearSelectedState');
    }
    this.debouncedSaveLastConversationView = debounce(route => {
      this.saveLastConversationView(route);
    }, 500);
  },
  async mounted() {
    const isValidRoute = await this.ensureValidConversationViewRoute(this.$route);
    const routeToSave = getRouteToSaveLastConversationView(this.$route);
    if (isValidRoute && routeToSave) {
      this.saveLastConversationView(routeToSave);
    }
    this.$store.dispatch('agents/get');
    this.initialize();
    this.$watch('$store.state.route', () => this.initialize());
    this.$watch('chatList.length', () => {
      this.setActiveChat();
    });
    this.resetContactPanelState();
    this.defaultConversation();
  },

  methods: {
    onConversationLoad() {
      if (
        this.hasFilterOrFolderView &&
        this.conversationId &&
        !this.findConversation()
      ) {
        this.$store.dispatch('clearSelectedState');
        const accountId = this.$route.params.accountId;
        if (!accountId) {
          return;
        }
        const targetPath = conversationListPageURL({
          accountId,
          conversationType: this.conversationType,
          inboxId: this.inboxId && Number(this.inboxId) !== 0 ? this.inboxId : undefined,
          label: this.label || undefined,
          teamId: this.teamId || undefined,
          customViewId:
            this.foldersId && Number(this.foldersId) !== 0
              ? this.foldersId
              : undefined,
        });
        if (targetPath && this.$route.path !== targetPath) {
          this.$router.push({ path: targetPath });
        }
        return;
      }
      this.fetchConversationIfUnavailable();
    },
    initialize() {
      this.$store.dispatch('setActiveInbox', this.inboxId);
      this.setActiveChat();
    },
    toggleConversationLayout() {
      const { LAYOUT_TYPES } = wootConstants;
      const {
        conversation_display_type:
          conversationDisplayType = LAYOUT_TYPES.CONDENSED,
      } = this.uiSettings;
      const newViewType =
        conversationDisplayType === LAYOUT_TYPES.CONDENSED
          ? LAYOUT_TYPES.EXPANDED
          : LAYOUT_TYPES.CONDENSED;
      this.updateUISettings({
        conversation_display_type: newViewType,
        previously_used_conversation_display_type: newViewType,
      });
    },
    async fetchConversationIfUnavailable() {
      if (!this.conversationId) {
        return;
      }
      const chat = this.findConversation();
      if (!chat) {
        await this.$store.dispatch('getConversation', this.conversationId);
      }
      this.setActiveChat();
    },
    findConversation() {
      const conversationId = parseInt(this.conversationId, 10);
      const [chat] = this.chatList.filter(
        c => Number(c.id) === Number(conversationId)
      );
      return chat;
    },
    setActiveChat() {
      const detached = this.$store.getters.getDetachedListHighlight;
      const keepDetachedHighlight =
        detached?.active &&
        detached.routePath &&
        (detached.routePath === this.$route.fullPath ||
          detached.routePath === this.$route.path);
      if (detached?.active && !keepDetachedHighlight) {
        this.$store.dispatch('clearDetachedListHighlight');
      }
      if (this.conversationId) {
        const selectedConversation = this.findConversation();
        // If conversation doesn't exist or selected conversation is same as the active
        // conversation, don't set active conversation.
        if (!selectedConversation) {
          return;
        }
        if (
          selectedConversation.id === this.currentChat.id &&
          this.currentChat.dataFetched === true
        ) {
          return;
        }
        const { messageId } = this.$route.query;
        this.$store
          .dispatch('setActiveChat', {
            data: selectedConversation,
            after: messageId,
          })
          .then(() => {
            bus.$emit(BUS_EVENTS.SCROLL_TO_MESSAGE, { messageId });
          });
      } else {
        this.$store.dispatch('clearSelectedState');
      }
    },
    onToggleContactPanel() {
      this.updateUISettings({
        is_contact_sidebar_open: !this.isContactPanelOpen,
      });
    },
    onSearch() {
      this.showSearchModal = true;
    },
    closeSearch() {
      this.showSearchModal = false;
    },
    resetContactPanelState() {
      this.updateUISettings({
        is_contact_sidebar_open: false, // Ensure panel is closed on page load or refresh
      });
    },
    defaultConversation(){
      const { LAYOUT_TYPES } = wootConstants;
      this.updateUISettings({
        conversation_display_type: LAYOUT_TYPES.CONDENSED,
        previously_used_conversation_display_type: LAYOUT_TYPES.CONDENSED,
      });
    },
    async clearLastConversationViewPreference() {
      const accountId = this.$route.params.accountId;
      if (!accountId) return;

      const { uiSettings } = buildUpdatedAccountPreferences(
        accountId,
        this.$store.getters.getCurrentUser,
        'last_conversation_view',
        { type: 'home' }
      );
      await this.$store.dispatch('updateUISettings', { uiSettings });
    },
    async ensureValidConversationViewRoute(route = this.$route) {
      const { accountId } = route.params;
      if (!accountId) return true;

      const routeName = route.name;
      let isInvalid = false;

      if (
        routeName === 'folder_conversations' ||
        routeName === 'conversations_through_folders'
      ) {
        let conversationFolders =
          this.$store.getters['customViews/getCustomViewsByFilterType'](
            'conversation'
          );
        if (!conversationFolderExists(conversationFolders, route.params.id)) {
          await this.$store.dispatch('customViews/get', 'conversation');
          conversationFolders =
            this.$store.getters['customViews/getCustomViewsByFilterType'](
              'conversation'
            );
        }
        isInvalid = !conversationFolderExists(
          conversationFolders,
          route.params.id
        );
      } else if (
        routeName === 'label_conversations' ||
        routeName === 'conversations_through_label'
      ) {
        try {
          await this.$store.dispatch('labels/getFresh');
        } catch {
          return true;
        }
        isInvalid = !labelExists(this.labels, route.params.label);
      } else if (
        routeName === 'inbox_dashboard' ||
        routeName === 'conversation_through_inbox'
      ) {
        if (!this.inboxes.length) {
          await this.$store.dispatch('inboxes/get');
        }
        isInvalid = !inboxExists(this.inboxes, route.params.inbox_id);
      } else if (
        routeName === 'team_conversations' ||
        routeName === 'conversations_through_team'
      ) {
        if (!this.teams.length) {
          await this.$store.dispatch('teams/get');
        }
        isInvalid = !teamExists(this.teams, route.params.teamId);
      }

      if (!isInvalid) return true;

      await this.clearLastConversationViewPreference();
      if (this.$route.name === route.name) {
        await this.$router.replace({
          name: 'home',
          params: { accountId },
        });
      }
      return false;
    },
    async saveLastConversationView(route = this.$route) {
      try {
        const viewData = getConversationViewDataFromRoute(route);
        if (!viewData) return;

        const accountId = route.params.accountId;
        if (!accountId) return;

        const currentUser = this.$store.getters.getCurrentUser;
        if (
          isSameAccountPreference(
            currentUser,
            accountId,
            'last_conversation_view',
            viewData
          )
        ) {
          return;
        }

        const { uiSettings } = buildUpdatedAccountPreferences(
          accountId,
          currentUser,
          'last_conversation_view',
          viewData
        );
        await this.$store.dispatch('updateUISettings', { uiSettings });
      } catch (error) {
        console.error('Failed to save last conversation view:', error);
      }
    },
  },
};
</script>
<style lang="scss" scoped>
.conversation-page {
  display: flex;
  flex-direction: row;
  width: 100%;
  height: 100%;
}
</style>
